import React, { useState, useEffect, useRef } from 'react';
import { PedidosService } from '../services/pedidosService';
import { ChoferesService } from '../services/choferesService';
import './GestionPedidos.css';

const GestionPedidos = () => {
  const [pedidos, setPedidos] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [filtros, setFiltros] = useState({
    estado: 'todos',
    fecha: '',
    cliente: '',
    chofer: '',
    folio: ''
  });
  const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
  const [mostrarModalPendiente, setMostrarModalPendiente] = useState(false);
  const [mostrarPedidosPendientes, setMostrarPedidosPendientes] = useState(false);
  const [comentarioPendiente, setComentarioPendiente] = useState('');
  const [formEntrega, setFormEntrega] = useState({
    fechaRecibido: '',
    fechaEntregaReal: '',
    horaEntregaReal: '',
    direccionEntrega: '',
    choferAsignado: '',
    estadoEntrega: 'en_preparacion',
    nombreRecibio: ''
  });
  const [recordatorioPendientes, setRecordatorioPendientes] = useState(false);
  const recordatorioMostradoRef = useRef(false);
  const pendientesSectionRef = useRef(null);
  const tableScrollTopRef = useRef(null);
  const tableScrollBottomRef = useRef(null);
  const isScrollingRef = useRef(false);
  
  const normalize = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const [usuarioData, setUsuarioData] = useState(() => JSON.parse(localStorage.getItem('usuario') || '{}'));
  const esLogistica = normalize(usuarioData.rol || '').includes('logistica');
  const esMostrador = normalize(usuarioData.rol || '').includes('mostrador');
  
  useEffect(() => {
    setUsuarioData(JSON.parse(localStorage.getItem('usuario') || '{}'));
  }, []);

  useEffect(() => {
    const unsubscribePedidos = PedidosService.suscribirsePedidos(setPedidos);
    const unsubscribeChoferes = ChoferesService.suscribirseChoferes(setChoferes);

    return () => {
      unsubscribePedidos();
      unsubscribeChoferes();
    };
  }, []);

  // Preferencias iniciales para usuario de Logística: ver todos los pedidos por defecto
  useEffect(() => {
    if (esLogistica) {
      // Cambiar a "todos" para que vea los pedidos nuevos de cajas (que tienen estatus "en_preparacion")
      setFiltros(prev => ({ ...prev, estado: 'todos' }));
      setMostrarPedidosPendientes(false);
    }
    // limpiar bandera al montar vista
    recordatorioMostradoRef.current = false;
  }, [esLogistica]);

  // Recordatorio al iniciar sesión si hay pedidos pendientes
  useEffect(() => {
    if (!pedidos || pedidos.length === 0) return;
    const numPendientes = pedidos.filter(p => p.estatus === 'pendiente').length;
    if (numPendientes > 0 && esLogistica && !recordatorioMostradoRef.current) {
      setRecordatorioPendientes(true);
      recordatorioMostradoRef.current = true;
      // Notificación del navegador (si el usuario otorgó permiso)
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification('Pedidos pendientes', { body: `Tienes ${numPendientes} pedido(s) pendiente(s) por atender.` });
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission();
        }
      }
    }
  }, [pedidos]);

  // Sincronizar scroll horizontal entre scrollbar superior e inferior
  useEffect(() => {
    const scrollTop = tableScrollTopRef.current;
    const scrollBottom = tableScrollBottomRef.current;
    
    if (!scrollTop || !scrollBottom) return;

    // Sincronizar el ancho del scrollbar superior con el ancho de la tabla
    const syncScrollbarWidth = () => {
      const table = scrollBottom.querySelector('.pedidos-table');
      if (table) {
        const tableWidth = table.scrollWidth;
        const scrollbarContent = scrollTop.querySelector('.table-scrollbar-content');
        if (scrollbarContent) {
          scrollbarContent.style.width = `${tableWidth}px`;
        }
      }
    };

    // Sincronizar el ancho inicialmente y después de cambios
    syncScrollbarWidth();
    
    // Observar cambios en el tamaño de la tabla
    const resizeObserver = new ResizeObserver(() => {
      syncScrollbarWidth();
    });

    const table = scrollBottom.querySelector('.pedidos-table');
    if (table) {
      resizeObserver.observe(table);
    }

    const handleScrollTop = () => {
      if (!isScrollingRef.current) {
        isScrollingRef.current = true;
        scrollBottom.scrollLeft = scrollTop.scrollLeft;
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 10);
      }
    };

    const handleScrollBottom = () => {
      if (!isScrollingRef.current) {
        isScrollingRef.current = true;
        scrollTop.scrollLeft = scrollBottom.scrollLeft;
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 10);
      }
    };

    scrollTop.addEventListener('scroll', handleScrollTop);
    scrollBottom.addEventListener('scroll', handleScrollBottom);

    return () => {
      scrollTop.removeEventListener('scroll', handleScrollTop);
      scrollBottom.removeEventListener('scroll', handleScrollBottom);
      resizeObserver.disconnect();
    };
  }, [pedidos.length, filtros]);

  const handleCambiarEstatus = async (pedidoId, nuevoEstatus) => {
    try {
      console.log(`Cambiando estatus del pedido ${pedidoId} a: ${nuevoEstatus}`);
      
      // Actualizar el pedido en Firebase
      await PedidosService.actualizarPedido(pedidoId, { 
        estatus: nuevoEstatus,
        fechaActualizacion: new Date().toISOString(),
        // Agregar timestamp para sincronización con app móvil
        timestamp: Date.now()
      });

      // Log para debugging del flujo
      const pedido = pedidos.find(p => p.id === pedidoId);
      console.log('=== FLUJO DE NOTIFICACIÓN ===');
      console.log('1. Admin Web cambia estado del pedido:', pedidoId);
      console.log('2. Pedido actualizado en Firebase:', {
        folio: pedido?.numeroDocumento || pedidoId,
        usuarioId: pedido?.telefonoCliente,
        status: nuevoEstatus,
        timestamp: Date.now()
      });
      console.log('3. App móvil escucha cambios en Firebase');
      console.log('4. Notificación enviada a:', pedido?.telefonoCliente);
      console.log(`5. Mensaje: "Tu pedido ${pedido?.numeroDocumento || pedidoId} fue ${getStatusText(nuevoEstatus).toUpperCase()}"`);
      console.log('===============================');

      alert(`Estatus actualizado a: ${getStatusText(nuevoEstatus)}`);
    } catch (error) {
      console.error('Error al cambiar estatus:', error);
      alert('Error al actualizar el estatus del pedido');
    }
  };

  const getStatusText = (estatus) => {
    const statusMap = {
      'en_preparacion': 'En Preparación',
      'ruta': 'En Ruta',
      'entregado': 'Entregado',
      'cancelado': 'Cancelado',
      'pendiente': 'Pendiente'
    };
    return statusMap[estatus] || estatus;
  };

  const abrirModalPedido = (pedido) => {
    setPedidoSeleccionado(pedido);
    setFormEntrega({
      fechaRecibido: pedido.fechaCompra || '',
      fechaEntregaReal: pedido.fechaEntrega || '',
      horaEntregaReal: pedido.horaEntregaReal || '',
      direccionEntrega: pedido.direccion || '',
      choferAsignado: pedido.chofer || '',
      estadoEntrega: pedido.estatus || 'en_preparacion',
      nombreRecibio: pedido.nombreRecibio || ''
    });
  };

  const handleChangeEntrega = (e) => {
    const { name, value } = e.target;
    setFormEntrega(prev => ({ ...prev, [name]: value }));
  };

  const handleRegistrarEntrega = async (e) => {
    e.preventDefault();
    if (!pedidoSeleccionado) return;

    const datosActualizacion = {
      fechaRecibido: formEntrega.fechaRecibido,
      fechaEntregaReal: formEntrega.fechaEntregaReal,
      horaEntregaReal: formEntrega.horaEntregaReal,
      direccion: formEntrega.direccionEntrega,
      chofer: formEntrega.choferAsignado,
      estatus: formEntrega.estadoEntrega,
      nombreRecibio: formEntrega.nombreRecibio
    };

    try {
      await PedidosService.actualizarPedido(pedidoSeleccionado.id, datosActualizacion);
      alert('Datos de entrega registrados');
      setPedidoSeleccionado(null);
    } catch (err) {
      console.error(err);
      alert('No se pudieron registrar los datos de entrega');
    }
  };

  const abrirModalPendiente = (pedido) => {
    setPedidoSeleccionado(pedido);
    setComentarioPendiente('');
    setMostrarModalPendiente(true);
  };

  const handleMarcarPendiente = async (e) => {
    e.preventDefault();
    if (!pedidoSeleccionado || !comentarioPendiente.trim()) {
      alert('Por favor, explica por qué el pedido está pendiente');
      return;
    }

    try {
      await PedidosService.actualizarPedido(pedidoSeleccionado.id, {
        estatus: 'pendiente',
        comentarioPendiente: comentarioPendiente,
        fechaPendiente: new Date().toISOString(),
        timestamp: Date.now()
      });
      
      alert('Pedido marcado como pendiente');
      setMostrarModalPendiente(false);
      setPedidoSeleccionado(null);
      setComentarioPendiente('');
    } catch (error) {
      console.error('Error al marcar como pendiente:', error);
      alert('Error al marcar el pedido como pendiente');
    }
  };

  const togglePedidosPendientes = () => {
    setMostrarPedidosPendientes(!mostrarPedidosPendientes);
    setTimeout(() => {
      if (pendientesSectionRef.current) {
        pendientesSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);
  };

  const getStatusClass = (estatus) => {
    switch (estatus) {
      case 'entregado':
        return 'status-entregado';
      case 'en_preparacion':
        return 'status-preparacion';
      case 'ruta':
        return 'status-ruta';
      case 'cancelado':
        return 'status-cancelado';
      case 'pendiente':
        return 'status-pendiente';
      default:
        return 'status-default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const dateStr = String(dateString).trim();
    
    // Extraer fecha directamente del formato YYYY-MM-DD para evitar problemas de zona horaria
    if (dateStr.length >= 10 && dateStr[4] === '-' && dateStr[7] === '-') {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(5, 7);
      const day = dateStr.substring(8, 10);
      return `${day}/${month}/${year}`;
    }
    
    // Fallback: usar Date con UTC si no es formato YYYY-MM-DD
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const d = String(date.getUTCDate()).padStart(2, '0');
      const m = String(date.getUTCMonth() + 1).padStart(2, '0');
      const y = date.getUTCFullYear();
      return `${d}/${m}/${y}`;
    } catch (e) {
      return '-';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const pedidosFiltradosBase = pedidos.filter(pedido => {
    if (filtros.estado !== 'todos' && pedido.estatus !== filtros.estado) return false;
    if (filtros.fecha && pedido.fechaCompra !== filtros.fecha) return false;
    if (filtros.cliente && !pedido.cliente.toLowerCase().includes(filtros.cliente.toLowerCase())) return false;
    if (filtros.chofer && pedido.chofer !== filtros.chofer) return false;
    if (filtros.folio) {
      const folioPedido = (pedido.numeroDocumento || pedido.id || '').toString().toLowerCase();
      const folioBusqueda = filtros.folio.toLowerCase().trim();
      if (!folioPedido.includes(folioBusqueda)) return false;
    }
    return true;
  });

  // Orden: primero en_preparacion (nuevos de cajas), luego pendientes, luego el resto por fecha descendente
  const pedidosFiltrados = [...pedidosFiltradosBase].sort((a, b) => {
    // Prioridad 1: En preparación (pedidos nuevos de cajas)
    const aPrep = a.estatus === 'en_preparacion' ? 2 : 0;
    const bPrep = b.estatus === 'en_preparacion' ? 2 : 0;
    
    // Prioridad 2: Pendientes (pedidos que requieren atención)
    const aPend = a.estatus === 'pendiente' ? 1 : 0;
    const bPend = b.estatus === 'pendiente' ? 1 : 0;
    
    const aPrioridad = aPrep + aPend;
    const bPrioridad = bPrep + bPend;
    
    if (aPrioridad !== bPrioridad) return bPrioridad - aPrioridad;
    
    // Si tienen la misma prioridad, ordenar por timestamp/fecha más reciente primero
    const aTime = a.timestamp || new Date(a.fechaPendiente || a.fechaCompra || 0).getTime();
    const bTime = b.timestamp || new Date(b.fechaPendiente || b.fechaCompra || 0).getTime();
    return bTime - aTime;
  });

  const estadisticas = {
    total: pedidos.length,
    enPreparacion: pedidos.filter(p => p.estatus === 'en_preparacion').length,
    enRuta: pedidos.filter(p => p.estatus === 'ruta').length,
    entregados: pedidos.filter(p => p.estatus === 'entregado').length,
    cancelados: pedidos.filter(p => p.estatus === 'cancelado').length,
    pendientes: pedidos.filter(p => p.estatus === 'pendiente').length
  };

  return (
    <div className="gestion-pedidos">
      <div className="header-section-modern">
        <div className="header-title-section">
          <h1>Gestión de Pedidos</h1>
        </div>
        <div className="estadisticas-header-modern">
          {!esMostrador && (
            <div className="stat-card-modern">
              <span className="stat-label-modern">TOTAL</span>
              <span className="stat-value-modern stat-total">{estadisticas.total}</span>
            </div>
          )}
          <div className="stat-card-modern">
            <span className="stat-label-modern">EN PREPARACIÓN</span>
            <span className="stat-value-modern stat-preparacion">{estadisticas.enPreparacion}</span>
          </div>
          <div className="stat-card-modern">
            <span className="stat-label-modern">EN RUTA</span>
            <span className="stat-value-modern stat-ruta">{estadisticas.enRuta}</span>
          </div>
          <div className="stat-card-modern">
            <span className="stat-label-modern">ENTREGADOS</span>
            <span className="stat-value-modern stat-entregado">{estadisticas.entregados}</span>
          </div>
          <div className="stat-card-modern">
            <span className="stat-label-modern">CANCELADOS</span>
            <span className="stat-value-modern stat-cancelado">{estadisticas.cancelados}</span>
          </div>
          <div className="stat-card-modern">
            <span className="stat-label-modern">PENDIENTES</span>
            <span className="stat-value-modern stat-pendiente">{estadisticas.pendientes}</span>
          </div>
        </div>
      </div>

      {/* Botón para ver pedidos pendientes */}
      {!esMostrador && (
        <div className="pendientes-section">
          <button 
            className="btn-pendientes"
            onClick={togglePedidosPendientes}
          >
            {mostrarPedidosPendientes ? 'Ocultar Pedidos Pendientes' : 'Ver Todos los Pedidos Pendientes'}
          </button>
        </div>
      )}

      {/* Filtros */}
      <div className="filtros-section">
        <h2>Filtros de Búsqueda</h2>
        <div className="filtros-grid">
          <div className="filter-group">
            <label>Número de Folio:</label>
            <input 
              type="text"
              placeholder="Buscar por folio"
              value={filtros.folio}
              onChange={(e) => setFiltros({...filtros, folio: e.target.value})}
            />
          </div>

          <div className="filter-group">
            <label>Estado:</label>
            <select 
              value={filtros.estado}
              onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
            >
              <option value="todos">Todos los Estados</option>
              <option value="en_preparacion">En Preparación</option>
              <option value="ruta">En Ruta</option>
              <option value="entregado">Entregado</option>
              <option value="cancelado">Cancelado</option>
              <option value="pendiente">Pendiente</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Fecha:</label>
            <input 
              type="date"
              value={filtros.fecha}
              onChange={(e) => setFiltros({...filtros, fecha: e.target.value})}
            />
          </div>

          <div className="filter-group">
            <label>Cliente:</label>
            <input 
              type="text"
              placeholder="Nombre del cliente"
              value={filtros.cliente}
              onChange={(e) => setFiltros({...filtros, cliente: e.target.value})}
            />
          </div>

          <div className="filter-group">
            <label>Chofer:</label>
            <select
              value={filtros.chofer}
              onChange={(e) => setFiltros({...filtros, chofer: e.target.value})}
            >
              <option value="">Todos los choferes</option>
                      {choferes.map(chofer => (
                        <option key={chofer.id} value={`${chofer.nombre} ${chofer.apellido || ''}`.trim()}>
                          {chofer.nombre} {chofer.apellido || ''}
                        </option>
                      ))}
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Pedidos */}
      <div className="pedidos-section">
        {!esMostrador && recordatorioPendientes && (
          <div className="alerta-prioridad" onClick={() => setRecordatorioPendientes(false)}>
            <strong>Prioridad:</strong> Tienes pedidos pendientes por atender. Haz clic para cerrar.
          </div>
        )}
        <h2>Lista de Pedidos ({pedidosFiltrados.length})</h2>
        
        {pedidosFiltrados.length === 0 ? (
          <div className="no-data">
            <p>No hay pedidos que coincidan con los filtros</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <div 
              className="table-scrollbar-top" 
              ref={tableScrollTopRef}
            >
              <div className="table-scrollbar-content"></div>
            </div>
            <div 
              className="table-container" 
              ref={tableScrollBottomRef}
            >
              <table className="pedidos-table">
              <thead>
                <tr>
                  <th>ID Pedido</th>
                  <th>Cliente</th>
                  <th>Chofer</th>
                  <th>Fecha</th>
                  <th>Importe</th>
                  <th>Estado Actual</th>
                  {!esMostrador && <th>Cambiar Estado</th>}
                  <th>Teléfono Cliente</th>
                  {esLogistica && <th>Referencias</th>}
                  {!esMostrador && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {pedidosFiltrados.map((pedido) => (
                  <tr key={pedido.id}>
                    <td className="pedido-id" data-label="ID Pedido">{pedido.numeroDocumento || pedido.id}</td>
                    <td className="cliente" data-label="Cliente">{pedido.cliente}</td>
                    <td className="chofer" data-label="Chofer">{pedido.chofer}</td>
                    <td className="fecha" data-label="Fecha">
                      {pedido.fechaCreacion 
                        ? formatDateTime(pedido.fechaCreacion)
                        : formatDate(pedido.fechaCompra)
                      }
                    </td>
                    <td className="importe" data-label="Importe">{formatCurrency(pedido.importe)}</td>
                    <td className="estado-actual" data-label="Estado Actual">
                      <span className={`status-badge ${getStatusClass(pedido.estatus)}`}>
                        {getStatusText(pedido.estatus)}
                      </span>
                    </td>
                    {!esMostrador && (
                      <td className="cambiar-estado" data-label="Cambiar Estado">
                        <select 
                          value={pedido.estatus}
                          onChange={(e) => handleCambiarEstatus(pedido.id, e.target.value)}
                          className="status-select"
                        >
                          <option value="en_preparacion">En Preparación</option>
                          <option value="ruta">En Ruta</option>
                          <option value="entregado">Entregado</option>
                          <option value="cancelado">Cancelado</option>
                        </select>
                      </td>
                    )}
                    <td className="telefono" data-label="Teléfono Cliente">{pedido.telefonoCliente}</td>
                    {esLogistica && (
                      <td className="referencias" data-label="Referencias">
                        {pedido.referencias || '-'}
                      </td>
                    )}
                    {!esMostrador && (
                      <td className="acciones" data-label="Acciones">
                        <button 
                          className="btn-details"
                          onClick={() => abrirModalPedido(pedido)}
                        >
                          Ver Detalles
                        </button>
                        <button
                          className="btn-pendiente"
                          onClick={() => abrirModalPendiente(pedido)}
                        >
                          Pendiente
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Sección de Pedidos Pendientes */}
      {!esMostrador && mostrarPedidosPendientes && (
        <div className="pedidos-pendientes-section" ref={pendientesSectionRef}>
          <h2>Pedidos Pendientes ({estadisticas.pendientes})</h2>
          {estadisticas.pendientes === 0 ? (
            <div className="no-data">
              <p>No hay pedidos pendientes</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="pedidos-table">
                <thead>
                  <tr>
                    <th>ID Pedido</th>
                    <th>Cliente</th>
                    <th>Fecha</th>
                    <th>Importe</th>
                    <th>Razón de Pendiente</th>
                    <th>Fecha Pendiente</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidos.filter(p => p.estatus === 'pendiente').map((pedido) => (
                    <tr key={pedido.id}>
                      <td className="pedido-id" data-label="ID Pedido">{pedido.numeroDocumento || pedido.id}</td>
                      <td className="cliente" data-label="Cliente">{pedido.cliente}</td>
                      <td className="fecha" data-label="Fecha">
                        {pedido.fechaCreacion 
                          ? formatDateTime(pedido.fechaCreacion)
                          : formatDate(pedido.fechaCompra)
                        }
                      </td>
                      <td className="importe" data-label="Importe">{formatCurrency(pedido.importe)}</td>
                      <td className="comentario-pendiente" data-label="Razón de Pendiente">{pedido.comentarioPendiente || 'Sin comentario'}</td>
                      <td className="fecha-pendiente" data-label="Fecha Pendiente">{formatDate(pedido.fechaPendiente)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal de Detalles */}
      {pedidoSeleccionado && (
        <div className="modal-overlay" onClick={() => setPedidoSeleccionado(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalles del Pedido</h2>
              <button className="close-button" onClick={() => setPedidoSeleccionado(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detalles-grid">
                <div className="detalle-item">
                  <label>ID Pedido:</label>
                  <span>{pedidoSeleccionado.numeroDocumento || pedidoSeleccionado.id}</span>
                </div>
                <div className="detalle-item">
                  <label>Cliente:</label>
                  <span>{pedidoSeleccionado.cliente}</span>
                </div>
                <div className="detalle-item">
                  <label>Chofer:</label>
                  <span>{pedidoSeleccionado.chofer}</span>
                </div>
                <div className="detalle-item">
                  <label>Teléfono:</label>
                  <span>{pedidoSeleccionado.telefonoCliente}</span>
                </div>
                <div className="detalle-item full-width">
                  <label>Dirección Completa:</label>
                  <span>{pedidoSeleccionado.direccionCompleta || pedidoSeleccionado.direccion || '-'}</span>
                </div>
                {pedidoSeleccionado.referencias && (
                  <div className="detalle-item full-width">
                    <label>Referencias:</label>
                    <span>{pedidoSeleccionado.referencias}</span>
                  </div>
                )}
                <div className="detalle-item">
                  <label>Fecha Compra:</label>
                  <span>{formatDate(pedidoSeleccionado.fechaCompra)}</span>
                </div>
                <div className="detalle-item">
                  <label>Fecha Entrega:</label>
                  <span>{formatDate(pedidoSeleccionado.fechaEntrega)}</span>
                </div>
                <div className="detalle-item">
                  <label>Importe:</label>
                  <span>{formatCurrency(pedidoSeleccionado.importe)}</span>
                </div>
                <div className="detalle-item">
                  <label>Estado:</label>
                  <span className={`status-badge ${getStatusClass(pedidoSeleccionado.estatus)}`}>
                    {getStatusText(pedidoSeleccionado.estatus)}
                  </span>
                </div>
                {pedidoSeleccionado.comentarios && (
                  <div className="detalle-item full-width">
                    <label>Comentarios:</label>
                    <span>{pedidoSeleccionado.comentarios}</span>
                  </div>
                )}
              </div>
              <hr />
              <h3>Registrar Datos de Entrega</h3>
              <form className="form-entrega" onSubmit={handleRegistrarEntrega}>
                <div className="filtros-grid">
                  <div className="filter-group">
                    <label>Fecha de Recibido *</label>
                    <input
                      type="date"
                      name="fechaRecibido"
                      value={formEntrega.fechaRecibido}
                      onChange={handleChangeEntrega}
                      required
                    />
                  </div>
                  <div className="filter-group">
                    <label>Fecha Entrega Real</label>
                    <input
                      type="date"
                      name="fechaEntregaReal"
                      value={formEntrega.fechaEntregaReal}
                      onChange={handleChangeEntrega}
                    />
                  </div>
                  <div className="filter-group">
                    <label>Hora de Entrega</label>
                    <input
                      type="time"
                      name="horaEntregaReal"
                      value={formEntrega.horaEntregaReal}
                      onChange={handleChangeEntrega}
                    />
                  </div>
                </div>
                <div className="filter-group">
                  <label>Dirección de Entrega *</label>
                  <input
                    type="text"
                    name="direccionEntrega"
                    placeholder="Dirección completa de entrega"
                    value={formEntrega.direccionEntrega}
                    onChange={handleChangeEntrega}
                    required
                  />
                </div>
                <div className="filter-group">
                  <label>Nombre de quien recibió</label>
                  <input
                    type="text"
                    name="nombreRecibio"
                    placeholder="Nombre de la persona que recibió"
                    value={formEntrega.nombreRecibio}
                    onChange={handleChangeEntrega}
                  />
                </div>
                <div className="filtros-grid">
                  <div className="filter-group">
                    <label>Chofer *</label>
                    <select
                      name="choferAsignado"
                      value={formEntrega.choferAsignado}
                      onChange={handleChangeEntrega}
                      required
                    >
                      <option value="">Seleccionar chofer...</option>
                      {choferes.map(chofer => (
                        <option key={chofer.id} value={`${chofer.nombre} ${chofer.apellido || ''}`.trim()}>
                          {chofer.nombre} {chofer.apellido || ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group">
                    <label>Estado de Entrega *</label>
                    <select
                      name="estadoEntrega"
                      value={formEntrega.estadoEntrega}
                      onChange={handleChangeEntrega}
                      required
                    >
                      <option value="en_preparacion">En Preparación</option>
                      <option value="ruta">En Ruta</option>
                      <option value="entregado">Entregado</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>
                <div className="acciones">
                  <button type="submit" className="btn-details">Registrar Datos de Entrega</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Marcar como Pendiente */}
      {mostrarModalPendiente && pedidoSeleccionado && (
        <div className="modal-overlay" onClick={() => setMostrarModalPendiente(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Marcar Pedido como Pendiente</h2>
              <button className="close-button" onClick={() => setMostrarModalPendiente(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="pedido-info">
                <p><strong>Pedido:</strong> {pedidoSeleccionado.numeroDocumento || pedidoSeleccionado.id}</p>
                <p><strong>Cliente:</strong> {pedidoSeleccionado.cliente}</p>
                <p><strong>Importe:</strong> {formatCurrency(pedidoSeleccionado.importe)}</p>
              </div>
              
              <form onSubmit={handleMarcarPendiente}>
                <div className="form-group">
                  <label>Explica por qué el pedido está pendiente *</label>
                  <textarea
                    value={comentarioPendiente}
                    onChange={(e) => setComentarioPendiente(e.target.value)}
                    placeholder="Ej: Cliente no disponible, dirección incorrecta, producto agotado, etc."
                    rows="4"
                    required
                    className="comentario-textarea"
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-cancelar" onClick={() => setMostrarModalPendiente(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-pendiente">
                    Marcar como Pendiente
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionPedidos;





